import * as React from "react";
import { cn } from "@/lib/utils";
import { isNetworkFailure, NETWORK_FAILURE_HEADING, NETWORK_FAILURE_MESSAGE, reportShownError } from "@/lib/error-analytics";
import { X } from "lucide-react";
import { Icon } from "@/pages/prototype-icons";
import { Alert } from "@/components/ui/alert";

/**
 * Toast — Fastn Design System contract.
 *
 * There is deliberately no error variant. Per the DS, an error has to persist
 * and answer what / why / next, which a four-second toast that slides away
 * cannot do. `ToastVariant` excludes "error" at the type level, so
 * `showToast(msg, "error")` will not compile — use `showError()`, which renders
 * a persistent Alert instead.
 */
export type ToastVariant = "default" | "success" | "warning" | "info";

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  message: string;
  variant?: ToastVariant;
  onClose: () => void;
  duration?: number;
  /** Drop the corner positioning and fill the width — for the shared column in `Notifications`. */
  stacked?: boolean;
}

/* The design's toast is one neutral `.fx-toast` card; the status lives in the
   icon's colour alone (`toast(msg, tone)` in the prototype), not in a tinted
   border per variant. */
const ICONS: Record<ToastVariant, React.ReactNode> = {
  default: null,
  success: <span aria-hidden style={{ color: "var(--fgColor-success)", display: "grid", placeItems: "center" }}><Icon name="check-circle-fill" /></span>,
  warning: <span aria-hidden style={{ color: "var(--fgColor-attention)", display: "grid", placeItems: "center" }}><Icon name="alert" /></span>,
  info: <span aria-hidden style={{ color: "var(--fgColor-accent)", display: "grid", placeItems: "center" }}><Icon name="info" /></span>,
};

const TOAST_SURFACE =
  "fx-toast transition-[opacity,transform] duration-200";

/* Bottom-right corner, shared by the standalone toast and the stacked column.

   `bottom-24`, not `bottom-4`: the Intercom launcher is a fixed bottom-right
   bubble at a z-index near the 32-bit ceiling (see IntercomWidget, which already
   works around the same obstruction for bottom-aligned modal footers). A toast
   at bottom-4 renders BEHIND it with its right-hand end covered, so a
   confirmation for a one-shot action — "Test alert sent to ..." — read as though
   the button had done nothing. Raising z-index cannot win that fight and should
   not try; clearing the launcher vertically is the fix. 24 (96px) clears the
   bubble's ~56px plus its own 20px inset with room to spare. */
const CORNER = "fixed bottom-24 right-4 left-4 z-[9999] sm:left-auto";

export function Toast({
  className,
  variant = "default",
  message,
  onClose,
  duration = 5000,
  stacked = false,
  ...props
}: ToastProps): React.ReactElement {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    if (duration <= 0 || paused) return;
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 200);
    }, duration);
    return () => clearTimeout(timer);
  }, [onClose, duration, paused]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  return (
    <div
      className={cn(
        TOAST_SURFACE,
        stacked ? "w-full" : CORNER,
        visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        className,
      )}
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="toast"
      {...props}
    >
      {ICONS[variant]}
      <span className="flex-1">{message}</span>
      <button
        onClick={handleClose}
        className="p-0.5 opacity-40 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid="toast-close-btn"
        aria-label="Close"
      >
        <X className="size-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

export interface ToastState {
  message: string;
  variant: ToastVariant;
}

export interface ErrorState {
  /** What happened. */
  heading: string;
  /** Why, in plain words. */
  message: string;
  /** How to fix it. */
  action?: { label: string; onClick: () => void };
}

export function useToast() {
  const [toast, setToast] = React.useState<ToastState | null>(null);
  const [error, setError] = React.useState<ErrorState | null>(null);

  const showToast = React.useCallback(
    (message: string, variant: ToastVariant = "default") => {
      setToast({ message, variant });
    },
    [],
  );

  /** Errors persist until dismissed — they are never a toast. */
  const showError = React.useCallback(
    (message: string, opts?: { heading?: string; action?: ErrorState["action"]; reportAs?: "error_alert" | "unhandled_failure" }) => {
      // The browser's "Failed to fetch" says nothing to a person: say what happened and what to do.
      const network = isNetworkFailure(message);
      const shown = network ? NETWORK_FAILURE_MESSAGE : message;
      const heading = opts?.heading ?? (network ? NETWORK_FAILURE_HEADING : "Something didn't complete");
      reportShownError({ kind: opts?.reportAs ?? "error_alert", message: shown, heading, ...(network ? { failureCategory: "network" as const } : {}) });
      setError({
        heading,
        message: shown,
        action: opts?.action,
      });
    },
    [],
  );

  const hideToast = React.useCallback(() => setToast(null), []);
  const dismissError = React.useCallback(() => setError(null), []);

  return { toast, error, showToast, showError, hideToast, dismissError };
}

/**
 * Both notification surfaces for a page: the transient toast for confirmations,
 * and the persistent Alert for errors.
 */
export function Notifications({
  toast,
  error,
  onCloseToast,
  onDismissError,
}: {
  toast: ToastState | null;
  error: ErrorState | null;
  onCloseToast: () => void;
  onDismissError: () => void;
}): React.ReactElement | null {
  if (!toast && !error) return null;
  // One column, so a success confirmation is never painted underneath the
  // persistent error Alert — both are anchored bottom-right and would otherwise
  // occupy the same box at the same z-index.
  return (
    <div className={cn(CORNER, "flex max-w-md flex-col gap-2")}>
      {error ? (
        <div className="w-full" data-testid="error-region">
          <Alert
            tone="error"
            heading={error.heading}
            action={error.action}
            className={ERROR_SURFACE}
          >
            <div className="flex items-start gap-2">
              <span className="flex-1">{error.message}</span>
              <button
                onClick={onDismissError}
                className="shrink-0 p-0.5 opacity-40 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="error-dismiss-btn"
                aria-label="Dismiss error"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
            </div>
          </Alert>
        </div>
      ) : null}
      {toast ? (
        <Toast message={toast.message} variant={toast.variant} onClose={onCloseToast} stacked />
      ) : null}
    </div>
  );
}

const ERROR_SURFACE = "bg-canvas shadow-2xl"; // design-tokens-ignore: overlay exception
